/**
 * Aviso de cierre de sesión, con dos motivos:
 *   - 'idle': inactividad. "Mantener sesión" reinicia la inactividad.
 *   - 'absolute': duración máxima de la sesión. NO ofrece "Mantener sesión":
 *     ningún clic puede extender el tope absoluto (antes el botón lo ocultaba y
 *     el aviso reaparecía en el siguiente segundo, en bucle).
 *
 * Patrón de modal de seguridad: NO cierra por click fuera ni por Escape — el
 * usuario debe elegir explícitamente una acción.
 * El cierre automático lo dispara el contador en useIdleLogout (no este modal).
 */
import { useEffect, useRef } from 'react';
import type { SessionDeadlineKind } from '../lib/sessionDeadline';
import type { ConsultationSaveState } from '../hooks/useIdleLogout';

interface SessionTimeoutModalProps {
  kind: SessionDeadlineKind;
  secondsLeft: number;
  /** Duración máxima de la sesión del rol, en horas. */
  maxHours: number;
  onKeep: () => void;
  onLogout: () => void;
  /** Guardado de la consulta activa pedido al entrar en el aviso (PR-4). */
  consultationSave: ConsultationSaveState;
  /** Reintento manual del guardado (solo tras un fallo). */
  onRetrySave: () => void;
}

function formatCountdown(s: number): string {
  if (s >= 60) {
    const m = Math.floor(s / 60);
    const r = s % 60;
    return `${m}:${r.toString().padStart(2, '0')}`;
  }
  return `${s} s`;
}

export default function SessionTimeoutModal({
  kind,
  secondsLeft,
  maxHours,
  onKeep,
  onLogout,
  consultationSave,
  onRetrySave,
}: SessionTimeoutModalProps) {
  const isAbsolute = kind === 'absolute';
  const panelRef = useRef<HTMLDivElement>(null);

  // Por duración máxima el aviso suele llegar mientras el médico escribe: el foco
  // va al panel y NO al botón "Cerrar sesión ahora", para que una tecla
  // (Enter / espacio) no cierre la sesión sin querer.
  useEffect(() => {
    if (isAbsolute) panelRef.current?.focus();
  }, [isAbsolute]);

  const countdown = (
    <span className="font-semibold text-gray-900 tabular-nums">
      {formatCountdown(secondsLeft)}
    </span>
  );

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-timeout-title"
      aria-describedby="session-timeout-desc"
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className="w-full max-w-md bg-white rounded-2xl shadow-xl p-6 outline-none"
      >
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
            <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M12 6v.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="min-w-0">
            <h3 id="session-timeout-title" className="text-lg font-semibold text-gray-900">
              {isAbsolute
                ? 'Tu sesión alcanzó su duración máxima'
                : 'Tu sesión está por cerrarse por inactividad'}
            </h3>
            <p id="session-timeout-desc" className="mt-1 text-sm text-gray-600">
              {isAbsolute ? (
                <>
                  Por seguridad, se cerrará automáticamente en {countdown} porque
                  alcanzó el máximo de {maxHours} horas. Después podrás volver a
                  iniciar sesión.
                </>
              ) : (
                <>Por seguridad, se cerrará automáticamente en {countdown}.</>
              )}
            </p>
            {consultationSave !== 'none' && (
              <ConsultationSaveStatus state={consultationSave} onRetry={onRetrySave} />
            )}
            {!isAbsolute && consultationSave === 'none' && (
              <p className="mt-2 text-sm text-amber-700">
                Guarda tus cambios si estás trabajando en una consulta.
              </p>
            )}
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
          <button
            type="button"
            onClick={onLogout}
            className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cerrar sesión ahora
          </button>
          {!isAbsolute && (
            <button
              type="button"
              onClick={onKeep}
              autoFocus
              className="px-5 py-2.5 text-sm font-semibold text-white bg-emerald-700 rounded-lg hover:bg-emerald-800 transition-colors"
            >
              Mantener sesión
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/** Estado del guardado de la consulta activa (PR-4). Sin reintentos automáticos. */
function ConsultationSaveStatus({
  state,
  onRetry,
}: {
  state: Exclude<ConsultationSaveState, 'none'>;
  onRetry: () => void;
}) {
  if (state === 'saving') {
    return (
      <p className="mt-2 text-sm text-gray-600" role="status">
        Guardando la consulta…
      </p>
    );
  }
  if (state === 'saved') {
    return (
      <p className="mt-2 text-sm text-emerald-700" role="status">
        ✓ La consulta quedó guardada.
      </p>
    );
  }
  return (
    <div className="mt-2" role="alert">
      <p className="text-sm text-red-700">
        No se pudo guardar la consulta. Los cambios sin guardar se perderán al
        cerrarse la sesión.
      </p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-colors"
      >
        Reintentar guardado
      </button>
    </div>
  );
}
